import "./AdminRedeemableProductList.css";
import { adminGet } from '../adminFetch';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminRedeemableProductList() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRedeemableProducts = async () => {
      try {
        setLoading(true);

        // adminGet sends the real auth token and asks for inactive rows too
        const response = await adminGet(`${API_BASE_URL}/products/redeemable`);

        if (!response.ok) {
          throw new Error("Failed to fetch redeemable products");
        }

        const data = await response.json();

        const sortedProducts = [...data.products].sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setProducts(sortedProducts);
        setFilteredProducts(sortedProducts);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await adminGet(`${API_BASE_URL}/api/categories`);
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchRedeemableProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!products.length) return;

    let filtered = [...products];

    if (nameFilter) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (product) => product.categoryID === parseInt(categoryFilter)
      );
    }

    filtered.sort((a, b) => {
      const result = a.name.localeCompare(b.name);
      return sortOrder === "asc" ? result : -result;
    });

    setFilteredProducts(filtered);
  }, [products, nameFilter, categoryFilter, sortOrder]);

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const clearFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setSortOrder("asc");
  };

  const renderPointsRequired = (product) => {
    if (!product.pointsRequired || product.pointsRequired <= 0) {
      return <span className="admin-redeemable-list-no-price">Points not set</span>;
    }

    const isOutOfStock = product.stockQuantity <= 0;

    return (
      <div className="admin-redeemable-list-points">
        <i className="fas fa-coins"></i>
        {product.pointsRequired.toLocaleString()} points
        {isOutOfStock && (
          <span className="admin-redeemable-list-redemption-badge out-of-stock">
            Out of Stock
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-redeemable-list-container">
        <div className="admin-redeemable-list-loading">
          <i className="fas fa-spinner fa-spin fa-2x"></i>
          <p>Loading redeemable products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-redeemable-list-container">
        <div className="admin-redeemable-list-error">
          Error: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-redeemable-list-container">
      {/* Back to Dashboard Button */}
      <div className="admin-redeemable-list-header">
        <button
          className="admin-redeemable-list-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-redeemable-list-title">
        <h1>Product Management - Redeem</h1>
        <div className="admin-redeemable-list-divider"></div>
      </div>

      {/* Action Buttons */}
      <div className="admin-redeemable-list-actions">
        <div className="admin-redeemable-list-actions-left">
          <button
            className="admin-redeemable-list-btn-manage"
            onClick={() => navigate("/admin/products")}
          >
            <i className="fas fa-cogs"></i> Manage All Products
          </button>
        </div>
        <div className="admin-redeemable-list-actions-right">
          <button
            className="admin-redeemable-list-btn-add"
            onClick={() => navigate("/admin/add-product/redeemable")}
          >
            <i className="fas fa-plus"></i> Add New Redeemable Product
          </button>
          <button
            className="admin-redeemable-list-btn-delete"
            onClick={() => navigate("/admin/deleted-products/redeemable")}
          >
            <i className="fas fa-trash-alt"></i> View Deleted Redeemable Products
          </button>
        </div>
      </div>

      { }
      <div className="admin-redeemable-list-filter">
        <div className="admin-redeemable-list-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Filter by product name"
            value={nameFilter}
            onChange={handleNameFilterChange}
            className="admin-redeemable-list-filter-input"
          />
        </div>
        <div className="admin-redeemable-list-filter-group">
          <label>Category</label>
          <select
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
          >
            <option value="">All Categories</option>
            {categories
              .filter(category => !category.subCategories || category.subCategories.length === 0)
              .map((category) => (
                <option key={category.categoryID} value={category.categoryID}>
                  {category.name}
                </option>
              ))}
          </select>
        </div>
        <button
          className="admin-redeemable-list-filter-btn"
          onClick={clearFilters}
        >
          Search
        </button>
      </div>

      {/* Product List Section */}
      <div className="admin-redeemable-list-grid">
        {filteredProducts.length === 0 ? (
          <div className="admin-redeemable-list-no-results">
            <i className="fas fa-coins"></i>
            <p className="primary-message">No redeemable products found.</p>
          </div>
        ) : (
          /* Product Cards Section */
          filteredProducts.map((product) => (
            <div
              key={product.productID}
              className="admin-redeemable-list-card"
            >
              <div className="admin-redeemable-list-image-container">
                <img
                  src={`${product.mainImageUrl}`}
                  alt={product.name}
                  className="admin-redeemable-list-image"
                />
              </div>
              <div className="admin-redeemable-list-details">
                <h2 className="admin-redeemable-list-name">{product.name}</h2>
                <span className={`admin-list-status ${product.isActive === false ? "inactive" : "active"}`}>
                  {product.isActive === false ? "Inactive" : "Active"}
                </span>
                <div className="admin-redeemable-list-points">
                  {renderPointsRequired(product)}
                </div>
                <div className="admin-redeemable-list-info">
                  <div className="admin-redeemable-list-detail">
                    <span className="admin-redeemable-list-detail-label">
                      Product Code:
                    </span>
                    <span className="admin-redeemable-list-detail-value">
                      {product.productCode || 'N/A'}
                    </span>
                  </div>
                  <div className="admin-redeemable-list-detail">
                    <span className="admin-redeemable-list-detail-label">
                      Category:
                    </span>
                    <span className="admin-redeemable-list-detail-value">
                      {categories.find(cat => cat.categoryID === product.categoryID)?.name || 'Unknown'}
                    </span>
                  </div>
                  <div className="admin-redeemable-list-detail">
                    <span className="admin-redeemable-list-detail-label">
                      UOM:
                    </span>
                    <span className="admin-redeemable-list-detail-value">
                      {product.uom || 'N/A'}
                    </span>
                  </div>
                  <div className="admin-redeemable-list-detail">
                    <span className="admin-redeemable-list-detail-label">
                      Total Redemptions:
                    </span>
                    <span className="admin-redeemable-list-detail-value admin-redeemable-list-redemptions">
                      {product.totalRedemptions || 0}
                    </span>
                  </div>
                  <div className="admin-redeemable-list-detail">
                    <span className="admin-redeemable-list-detail-label">
                      Stock:
                    </span>
                    <span className="admin-redeemable-list-detail-value">
                      {product.stockQuantity ?? "0"}
                      {product.stockQuantity < 50 && product.stockQuantity > 0 && (
                        <span className="admin-redeemable-list-low-stock"> - Low Stock</span>
                      )}
                      {product.stockQuantity <= 0 && (
                        <span className="admin-redeemable-list-low-stock"> - Out of Stock</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="admin-redeemable-list-actions-card">
                <button
                  className="admin-redeemable-list-btn-edit"
                  onClick={() =>
                    navigate(`/admin/edit-product/redeemable/${product.productID}`)
                  }
                >
                  <i className="far fa-edit"></i> Edit Product
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminRedeemableProductList;